import React, { useMemo } from "react";

interface MobilePreviewProps {
  siteData: {
    name: string;
    slogan?: string;
    address?: string;
    detail_info?: string;
  };
  heroImages: string[];
  sectionVisibility: {
    hero_carousel?: { enabled: boolean; order: number };
    business_intro?: { enabled: boolean; order: number };
    notice?: { enabled: boolean; order: number };
    coupon?: { enabled: boolean; order: number };
    display_menu?: { enabled: boolean; order: number };
    reviews?: { enabled: boolean; order: number };
    facilities?: { enabled: boolean; order: number };
    sns_links?: { enabled: boolean; order: number };
    carousel_gallery?: { enabled: boolean; order: number };
    map?: { enabled: boolean; order: number };
  };
  bottomBarVisibility: {
    ticket: boolean;
    reservation: boolean;
    lodging: boolean;
    shopping: boolean;
    order?: boolean;
  };
  selectedAmenities: any[];
  mainTitleColor: string;
  showAddress: boolean;
  isLoading?: boolean;
}

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`bg-gray-200 animate-pulse ${className}`}></div>
);

const MobilePreview: React.FC<MobilePreviewProps> = ({
  siteData,
  heroImages,
  sectionVisibility,
  bottomBarVisibility,
  selectedAmenities,
  mainTitleColor,
  showAddress,
  isLoading = false,
}) => {
  const heroImage =
    heroImages?.[0] || "https://via.placeholder.com/400x300?text=No+Hero+Image";

  // Memoize visible sections computation
  const visibleSections = useMemo(() => {
    const sections: Array<{
      key: keyof typeof sectionVisibility;
      label: string;
      component: React.ReactElement;
    }> = [
      {
        key: "hero_carousel",
        label: "히어로 캐러셀",
        component: <div key="hero_carousel" />,
      },
      {
        key: "business_intro",
        label: "업체소개",
        component: (
          <div key="business_intro" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">업체소개</h2>
            <p className="text-[10px] text-gray-600 leading-relaxed whitespace-pre-wrap">
              {siteData.detail_info || "업체 소개 내용이 여기에 표시됩니다."}
            </p>
            {selectedAmenities.length > 0 && (
              <div className="grid grid-cols-4 gap-y-4">
                {selectedAmenities.map((amenity) => (
                  <div
                    key={amenity.id}
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 border border-gray-100 text-[8px] text-gray-600 font-medium"
                  >
                    <span className="w-3 h-3 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {amenity.image ? (
                        <img
                          src={amenity.image}
                          alt={amenity.name}
                          className="w-full h-full object-contain"
                        />
                      ) : null}
                    </span>
                    <span className="truncate">{amenity.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "notice",
        label: "공지사항",
        component: (
          <div key="notice" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">공지사항</h2>
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 border-b border-gray-50 pb-2 last:border-0 last:pb-0"
                >
                  <div className="w-8 h-2 bg-gray-100 rounded"></div>
                  <div className="w-full h-2 bg-gray-50 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        key: "coupon",
        label: "쿠폰 / 이벤트",
        component: (
          <div key="coupon" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">쿠폰</h2>
            <div className="flex gap-2">
              <div className="h-10 w-full bg-orange-50 rounded border border-orange-100 flex items-center justify-center relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-200"></div>
                <span className="text-[8px] text-orange-400 font-bold">
                  첫 방문 할인 쿠폰 (다운로드형)
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "display_menu",
        label: "메뉴 안내",
        component: (
          <div key="display_menu" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">메뉴 안내</h2>
            <div className="flex gap-2 overflow-hidden">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="w-28 shrink-0 rounded-xl border border-gray-100 bg-gray-50 overflow-hidden"
                >
                  <div className="h-20 bg-gray-100" />
                  <div className="p-2 space-y-1.5">
                    <div className="h-2 w-2/3 bg-gray-300/50 rounded" />
                    <div className="h-2 w-full bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        key: "facilities",
        label: "시설 안내",
        component: (
          <div key="facilities" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">시설 안내</h2>
            <div className="grid grid-cols-2 gap-2">
              <div className="h-20 bg-gray-50 rounded-lg overflow-hidden flex items-end p-2 border border-gray-100">
                <div className="h-2 w-12 bg-gray-300/50 rounded"></div>
              </div>
              <div className="h-20 bg-gray-50 rounded-lg overflow-hidden flex items-end p-2 border border-gray-100">
                <div className="h-2 w-12 bg-gray-300/50 rounded"></div>
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "reviews",
        label: "추천 리뷰",
        component: (
          <div key="reviews" className="p-4 bg-white mt-2 pb-6">
            <h2 className="text-xs font-bold text-gray-900 mb-2">추천 리뷰</h2>
            <div className="space-y-2">
              <div className="h-16 bg-white border border-gray-100 rounded-lg p-2 flex gap-2">
                <div className="w-12 h-12 bg-gray-200 rounded shrink-0"></div>
                <div className="space-y-1.5 w-full flex flex-col justify-center">
                  <div className="h-2 w-1/3 bg-gray-200 rounded"></div>
                  <div className="h-2 w-full bg-gray-100 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "sns_links",
        label: "SNS 링크",
        component: (
          <div key="sns_links" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">SNS</h2>
            <div className="flex gap-2">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center"
                ></div>
              ))}
            </div>
          </div>
        ),
      },
      {
        key: "carousel_gallery",
        label: "갤러리 캐러셀",
        component: (
          <div key="carousel_gallery" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">갤러리</h2>
            <div className="h-24 bg-gray-100 rounded-lg"></div>
          </div>
        ),
      },
      {
        key: "map",
        label: "위치 지도",
        component: (
          <div key="map" className="p-4 bg-white mt-2">
            <h2 className="text-xs font-bold text-gray-900 mb-2">위치</h2>
            <div className="h-20 bg-gray-100 rounded-lg"></div>
          </div>
        ),
      },
    ];

    return sections
      .filter((section) => sectionVisibility[section.key]?.enabled)
      .sort((a, b) => {
        const orderA = sectionVisibility[a.key]?.order ?? 99;
        const orderB = sectionVisibility[b.key]?.order ?? 99;
        return orderA - orderB;
      });
  }, [sectionVisibility, siteData]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center w-full">
        <div className="text-sm font-semibold text-gray-500 mb-4">
          모바일 미리보기
        </div>

        {/* Phone Frame - Loading State */}
        <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden shrink-0">
          <div className="w-[148px] h-[18px] bg-gray-800 top-0 left-1/2 -translate-x-1/2 absolute rounded-b-xl z-20"></div>

          {/* Screen Content - Skeleton */}
          <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white dark:bg-gray-900 overflow-y-auto no-scrollbar pb-12 p-4">
            {/* Hero Skeleton */}
            <Skeleton className="w-full h-48 rounded-lg mb-4" />

            {/* Quick Info Skeleton */}
            <Skeleton className="w-full h-8 rounded-lg mb-4" />

            {/* Amenities Skeleton */}
            <div className="space-y-2 mb-4">
              <Skeleton className="w-20 h-4 rounded" />
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="w-12 h-3 rounded" />
                  </div>
                ))}
              </div>
            </div>

            {/* Section Skeletons */}
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2 mb-4">
                <Skeleton className="w-24 h-5 rounded" />
                <Skeleton className="w-full h-20 rounded-lg" />
              </div>
            ))}
          </div>

          {/* Bottom Navigation - Skeleton */}
          <div className="absolute bottom-0 left-0 right-0 h-14 bg-white border-t border-gray-100 flex items-center justify-around px-2 z-30">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <Skeleton className="w-5 h-5 rounded-full" />
                <Skeleton className="w-8 h-2 rounded" />
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-gray-400 mt-4 max-w-[200px] text-center">
          * 모바일 미리보기는 레이아웃 참고용입니다.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full">
      <div className="text-sm font-semibold text-gray-500 mb-4">
        모바일 미리보기
      </div>

      {/* Phone Frame */}
      <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden shrink-0">
        <div className="w-[148px] h-[18px] bg-gray-800 top-0 left-1/2 -translate-x-1/2 absolute rounded-b-xl z-20"></div>

        {/* Screen Content */}
        <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white dark:bg-gray-900 overflow-y-auto no-scrollbar pb-12">
          {/* Header/Hero Section */}
          <div className="relative w-full h-48 bg-gray-200">
            <img
              src={heroImage}
              alt="Hero"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center p-4 text-center">
              <h1
                className="text-xl font-bold break-all"
                style={{ color: mainTitleColor }}
              >
                {siteData.name || "업체명"}
              </h1>
              {siteData.slogan && (
                <p className="text-white text-xs mt-1 line-clamp-2">
                  {siteData.slogan}
                </p>
              )}
            </div>
          </div>

          {/* Quick Info Bar */}
          <div className="px-4 py-3 bg-white border-b border-gray-100 italic">
            {showAddress && siteData.address && (
              <div className="text-[10px] text-gray-500 truncate">
                📍 {siteData.address}
              </div>
            )}
          </div>

          {/* Dynamic Sections - Ordered by visibility config */}
          {visibleSections.map((section) => (
            <React.Fragment key={section.key}>
              {section.component}
            </React.Fragment>
          ))}
        </div>

        {/* Bottom Navigation */}
        <div className="absolute bottom-0 left-0 right-0 h-14 bg-white border-t border-gray-100 flex items-center justify-around px-2 z-30 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          {/* Home - Always shown */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center text-white text-[10px]">
              🏠
            </div>
            <span className="text-[8px] text-green-600 font-bold">홈</span>
          </div>
          {bottomBarVisibility.ticket && (
            <div className="flex flex-col items-center gap-0.5 opacity-40">
              <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
                🎟️
              </div>
              <span className="text-[8px] text-gray-600">티켓</span>
            </div>
          )}
          {bottomBarVisibility.reservation && (
            <div className="flex flex-col items-center gap-0.5 opacity-40">
              <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
                📅
              </div>
              <span className="text-[8px] text-gray-600">예약</span>
            </div>
          )}
          {bottomBarVisibility.lodging && (
            <div className="flex flex-col items-center gap-0.5 opacity-40">
              <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
                🛌
              </div>
              <span className="text-[8px] text-gray-600">숙박</span>
            </div>
          )}
          {bottomBarVisibility.shopping && (
            <div className="flex flex-col items-center gap-0.5 opacity-40">
              <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
                🛍️
              </div>
              <span className="text-[8px] text-gray-600">쇼핑</span>
            </div>
          )}
          {bottomBarVisibility.order && (
            <div className="flex flex-col items-center gap-0.5 opacity-40">
              <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
                🍽️
              </div>
              <span className="text-[8px] text-gray-600">오더</span>
            </div>
          )}
          {/* MY - Always shown */}
          <div className="flex flex-col items-center gap-0.5 opacity-40">
            <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center text-white text-[10px]">
              👤
            </div>
            <span className="text-[8px] text-gray-600">MY</span>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-gray-400 mt-4 max-w-[200px] text-center">
        * 모바일 미리보기는 레이아웃 참고용입니다.
      </p>
    </div>
  );
};

export default MobilePreview;
